import { BUILDER_EVENT_TYPES, COURSE_API } from '../values/courses.values';
import { RequestProvider } from '@/shared/providers/request.provider';
import { ErrorProvider } from '@/shared/providers/errors.provider';
import { IRunSubjectBuilderService, ISubjectBuilderStateService } from '../interfaces/subject-builder.interfaces';
import { SERVER_TYPE } from '@/shared/enums/request.enum';
import { IResultDownloadableResourceForContent } from '../interfaces/result-downloadable-resource-for-content.interface';
import Vue from 'vue';
import { FILESCACHE_KEYS } from '@/shared/plugins/files-cache/enums/files-cache.enum';

class CourseServiceClass {

  async getClassContents(page: number = 0, pageSize: number = 100): Promise<any> {
    try {
      let req = COURSE_API.GET_COURSES_LIST
        .replace('{page}', String(page))
        .replace('{pageSize}', String(pageSize));
      return RequestProvider.get(req);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  getContentForCourse(courseId: number, classId: number = null): Promise<any> {
    let req = COURSE_API.GET_COURSE_SECTIONS
      .replace('{courseId}', String(courseId));
    if(classId) req += `?classId=${classId}`;
    return RequestProvider.get(req);
  } 
  
  getContentDownloadableResources(courseId: number, classId: number = null): Promise<IResultDownloadableResourceForContent> {
    let req = COURSE_API.GET_COURSE_DOWNLOADABLE_RESOURCES
      .replace('{courseId}', String(courseId));
    if(classId) req += `?classId=${classId}`;
    return RequestProvider.get(req);
  } 
    
  setTeacherOnlySection(
    courseId: number,
    sectionId: number,
    teacherOnly: boolean): Promise<any> {
    let req = COURSE_API.SET_TEACHER_ONLY
      .replace('{courseId}', String(courseId))
      .replace('{sectionId}', String(sectionId))
      .replace('{teacherOnly}', String(teacherOnly));
    return RequestProvider.put(req, { teacherOnly });
  }

  obtainSectionInfo(contentId: number, sectionId: number, classId: number): Promise<any> {
    return RequestProvider.get(COURSE_API.STATE_SECTION_FILES
      .replace('{courseId}', String(contentId))
      .replace('{sectionId}', String(sectionId))
      .replace('{classId}', String(classId)));
  }

  obtainSectionTree(contentId: number, sectionId: number): Promise<any> {
    return RequestProvider.get(COURSE_API.GET_SECTION_TREE
      .replace('{courseId}', String(contentId))
      .replace('{sectionId}', String(sectionId)));
  }

  obtainCoursesByClass(classId : number){
    return RequestProvider.get(COURSE_API.OBTAIN_COURSES_BY_CLASS
      .replace('{classId}', String(classId)));
  }

  obtainCourseMaterialInfo(courseId: number, materialId : number) : Promise<any> {
    return RequestProvider.get(COURSE_API.STATE_COURSE_MATERIAL
      .replace('{courseId}' , String(courseId))
      .replace('{materialId}' , String(materialId)));
  }

  getGradingDetailsForCourse(courseId: number, classId: number = null): Promise<any> {
    const path = COURSE_API.GET_GRADING_DETAILS.replace('{courseId}', String(courseId))
      .replace('{classId}', String(classId));
    return RequestProvider.get(path);
  }
  
  async getCourseCategories(orgId: number) : Promise<any> {
    const data = await Vue.prototype.$BuilderCache.getFile(`categories_${orgId}`, FILESCACHE_KEYS.MASTER_DATA);
    if(!data || !data.file) {
      const categories = await RequestProvider.get(COURSE_API.GET_COURSE_CATEGORIES
        .replace('{orgId}' , String(orgId)));
      await Vue.prototype.$BuilderCache.cacheFile(`categories_${orgId}`, categories, FILESCACHE_KEYS.MASTER_DATA);
      return categories;
    } else {
      return data.file;
    }
    
  }

  createCourse(content: any, image: any) {
    let data = new FormData();
    data.append('course', JSON.stringify(content))
    data.append('coverImage', image);
    return RequestProvider.post(COURSE_API.CREATE_COURSE, data, {}, { 'Content-Type': 'multipart/form-data' });
  }

  updateCourse(course: any, courseId: number, image: any) {
    let data = new FormData();
    data.append('course', JSON.stringify(course))
    data.append('coverImage', image);
    return RequestProvider.put(COURSE_API.UPDATE_COURSE
      .replace('{courseId}' , String(courseId)), data);
  }

  async getSubjectBuilder({
    templateId, 
    schoolDay = null, 
    workingDays = null, 
    specialDays = null, 
    subject = null, 
    coverImage = null, 
    rule = null, 
    lessons = null, 
    existChanges = true, 
    automaticFolderType = null,
    step = 0, 
    existFunLessons = null,
    additionalLessons = [],
    resourceId = null,
    rulesApplied = [],
    objectsTagged = [],
    componentsSelected = [],
    componentsChanged = false,
    quickContentId = null,
  }) {
    const req = COURSE_API.GET_SUBJECT_BUILDER
      .replace('{templateId}', String(templateId));
    let formData = new FormData();
    let builder = JSON.stringify({
      schoolDay, 
      workingDays, 
      specialDays, 
      subject, 
      rule, 
      lessons, 
      existChanges, 
      automaticFolderType,
      step, 
      existFunLessons,
      additionalLessons,
      resourceId,
      rulesApplied,
      objectsTagged,
      componentsSelected,
      componentsChanged,
      quickContentId
    });
    formData.append('builder', builder);
    formData.append('coverImage', coverImage);
    return await RequestProvider.post(req, formData, {}, { 'Content-Type': 'multipart/form-data' });
  }

  runSubjectBuilder(data: IRunSubjectBuilderService) {
    const req = COURSE_API.RUN_SUBJECT_BUILDER
      .replace('{templateId}', String(data.templateId));
    const { templateId: _, ...subjectData } = data;

    return RequestProvider.post(req, subjectData);
  }

  async getLogsFromUploadZip(sectionId: number, since: number, limit: number | string): 
    Promise<{ log: string, currentLength: number, currentEnd: number }> {
      const req = COURSE_API.GET_LOG_FROM_UPLOAD_ZIP
        .replace('{sectionId}', String(sectionId));

      return await this.getLogData(req, since, limit);
  }
  

  async getLogsFromSubjectBuilder(templateId: number, since: number, limit: number | string): 
    Promise<{ log: string, currentLength: number, currentEnd: number }> {
      const req = COURSE_API.GET_FROM_SUBJECT_BUILDER
        .replace('{templateId}', String(templateId));

      return await this.getLogData(req, since, limit);
  }

  async getLogData(req: any, since: number, limit: number | string) {
    const response = await RequestProvider.request('GET', req, {}, {}, {
      Range: `bytes=${since}-${ limit || '*' }`
    }, 0, SERVER_TYPE.COMMON, true);

    const headersResponse = response.headers;
    
    let currentLength = null; 
    let currentEnd = null;

    if (headersResponse['content-range']) {
      const ranges = headersResponse['content-range'].split('/');

      currentEnd = Number(ranges[0].split('-')[1]);
      currentLength = Number(ranges[1]);
    }

    return { log: response.data, currentLength, currentEnd };
  }

  getSubjectCalendar(contentId: number) {
    const req = COURSE_API.GET_SUBJECT_CALENDAR.replace('{contentId}', String(contentId));

    return RequestProvider.get(req);
  }

  getBuilderAllLessons(contentId: number, rootFixedSection: string) {
    const req = COURSE_API.GET_BUILDER_ALL_LESSONS
    .replace('{contentId}', String(contentId))
    .replace('{rootFixedSection}', String(rootFixedSection))

    return RequestProvider.get(req);
  }

  getBuilderBackup(templateId: number, resourceId: string) {
    const req = COURSE_API.GET_BUILDER_BACKUP
    .replace('{templateId}', String(templateId))
    .replace('{resourceId}', String(resourceId))

    return RequestProvider.get(req);
  }


  getSectionInfoByLink(url: string) : Promise<any> {
    return new Promise((resolve, reject) => {
      RequestProvider.requestPublicUrl(url)
      .then((response) => {
        resolve(response)
      })  
      .catch(() => reject());
    });
  }

  /**
   * Requests the first private URL for an HTML slide. Media chooses the URL that works with the user's current
   * session, replacing the old public link used by this viewer.
   */
  async getSlideAccess(hash: string): Promise<any> {
    return RequestProvider.postMedia('media/slides/access', { hash });
  }

  async buildSubjecFromTemplate({
    templateId, 
    subject, 
    coverImage, 
    componentsSelected = []}) {
    const req = COURSE_API.BUILD_SUBJECT_FROM_TEMPLATE
      .replace('{templateId}', String(templateId))
    let formData = new FormData();
    let courseRequest = JSON.stringify({
      templateId,
      subject, 
      componentsSelected});
    formData.append('courseRequest', courseRequest);
    formData.append('coverImage', coverImage);
    return await RequestProvider.post(req, formData, {}, { 'Content-Type': 'multipart/form-data' });
  }

}

export const CourseService = new CourseServiceClass();
